import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { AlertTriangleIcon, BoxesIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, PackageXIcon, PillIcon, ShieldCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { PharmacySection } from './PharmacyModule';
import { expiryAlertWindow, formatCurrency, isLowStockOnly, isOutOfStock, isToday } from './pharmacyUtils';
type Props = {
  onNavigate: (section: PharmacySection) => void;
  readOnly?: boolean;
};
export function PharmacyOverview({
  onNavigate,
  readOnly = false
}: Props) {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders,
    stockMovements,
    encounters,
    pharmacySettings,
    updatePharmacySettings
  } = useData();
  const [savingPolicy, setSavingPolicy] = useState(false);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const stats = useMemo(() => {
    const expiry = (window: Exclude<ReturnType<typeof expiryAlertWindow>, null>) => medicineBatches.filter(batch => expiryAlertWindow(batch) === window);
    return {
      lowStock: medicines.filter(isLowStockOnly),
      outOfStock: medicines.filter(isOutOfStock),
      expired: expiry('Expired'),
      days30: expiry('30 Days'),
      days60: expiry('60 Days'),
      days90: expiry('90 Days'),
      todayDispensed: stockMovements.filter(movement => movement.type === 'Dispense' && isToday(movement.createdAt)),
      todayReceived: stockMovements.filter(movement => movement.type === 'Receipt' && isToday(movement.createdAt)),
      pendingOrders: purchaseOrders.filter(order => order.status === 'Pending').length,
      pendingPrescriptions: encounters.filter(encounter => encounter.stage === 'PHARMACY').length,
      value: medicines.reduce((sum, medicine) => sum + medicine.currentQuantity * medicine.costPrice, 0)
    };
  }, [encounters, medicineBatches, medicines, purchaseOrders, stockMovements]);
  const updatePolicy = async (allowMedicineReturns: boolean) => {
    setSavingPolicy(true);
    await updatePharmacySettings({
      allowMedicineReturns
    });
    setSavingPolicy(false);
  };
  const cards = [{
    label: 'Total medicines',
    value: medicines.length,
    icon: PillIcon,
    tone: 'blue'
  }, {
    label: 'Inventory value',
    value: formatCurrency(stats.value),
    icon: CircleDollarSignIcon,
    tone: 'emerald'
  }, {
    label: 'Low stock',
    value: stats.lowStock.length,
    icon: AlertTriangleIcon,
    tone: 'amber'
  }, {
    label: 'Out of stock',
    value: stats.outOfStock.length,
    icon: PackageXIcon,
    tone: 'red'
  }, {
    label: 'Expired batches',
    value: stats.expired.length,
    icon: TriangleAlertIcon,
    tone: 'red'
  }, {
    label: 'Dispensed today',
    value: stats.todayDispensed.length,
    icon: PackageCheckIcon,
    tone: 'teal'
  }, {
    label: 'Received today',
    value: stats.todayReceived.length,
    icon: BoxesIcon,
    tone: 'blue'
  }] as const;
  return <div className="space-y-6" data-id="element-5129" data-mp="32hhohitb84sd">
      <section className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-5130" data-mp="32ed8lo6hfklv">
        <div data-id="element-5131" data-mp="3v553mibta37m">
          <p className="text-sm font-semibold text-pink-600" data-id="element-5132" data-mp="3v4ltvowbel4o">
            BMED Care pharmacy desk
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl" data-id="element-5133" data-mp="301ipln6pt7r">
            Inventory with every dispense accounted for.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600" data-id="element-5134" data-mp="3q5ino14lm8v4">
            Live stock levels, batch controls, policy enforcement, and patient
            dispensing in one operational view.
          </p>
        </div>
        {readOnly ? <div className="flex flex-wrap items-center gap-2" data-id="element-5135" data-mp="2es6qua79aqig">
            <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800" data-id="element-5136" data-mp="2u0s9plssqajk">
              View-only monitoring access
            </span>
            <button type="button" onClick={() => onNavigate('inventory')} className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700" data-id="element-5137" data-mp="2l906nw63q3p0">
              <PillIcon className="mr-1.5 h-4 w-4" data-id="element-5138" data-mp="38xjds8df0afi" /> View inventory
            </button>
            <button type="button" onClick={() => onNavigate('movement-history')} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-id="element-5139" data-mp="38vot8v622kby">
              <ClipboardListIcon className="mr-1.5 h-4 w-4" data-id="element-5140" data-mp="1skkqv7f5bhcm" /> Review activity
            </button>
          </div> : <div className="flex flex-wrap gap-2" data-id="element-5141" data-mp="1dg9qqtcec8wq">
            <button type="button" onClick={() => onNavigate('dispensing')} className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700" data-id="element-5142" data-mp="1dhkp2dn2oogg">
              <ClipboardListIcon className="mr-1.5 h-4 w-4" data-id="element-5143" data-mp="1s5nyjx0bqia0" /> Dispensing queue
            </button>
            <button type="button" onClick={() => onNavigate('inventory')} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-id="element-5144" data-mp="1w797j79qmcex">
              <PackageCheckIcon className="mr-1.5 h-4 w-4" data-id="element-5145" data-mp="2632p552bcw3k" /> View inventory
            </button>
          </div>}
      </section>
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-5146" data-mp="bro2wy8ihesk">
        {cards.map(({
        label,
        value,
        icon: Icon,
        tone
      }) => <article key={label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5147" data-mp="gs3crftl445s">
            <div className="flex items-start justify-between gap-3" data-id="element-5148" data-mp="3qq5gvo5rfky">
              <div data-id="element-5149" data-mp="3skrkdb61dqm">
                <p className="text-sm font-medium text-slate-500" data-id="element-5150" data-mp="in7h7o09ew81">{label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-slate-950" data-id="element-5151" data-mp="il4ga8xkjioj">
                  {value}
                </p>
              </div>
              <div className={`rounded-lg p-2.5 ${iconTone(tone)}`} data-id="element-5152" data-mp="rn3yh4kbkst2">
                <Icon className="h-5 w-5" data-id="element-5153" data-mp="mpc5s8bup2nt" />
              </div>
            </div>
          </article>)}
      </section>
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_0.85fr]" data-id="element-5154" data-mp="11e8g6c1dqrrl">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5155" data-mp="wec8a1pkag6r">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-5156" data-mp="1aen0b82ly0ti">
            <div data-id="element-5157" data-mp="16d1sw28ueeqp">
              <h3 className="font-bold text-slate-900" data-id="element-5158" data-mp="16dlafhukz147">
                Automated operational alerts
              </h3>
              <p className="mt-0.5 text-sm text-slate-500" data-id="element-5159" data-mp="1p2f18brt5yu2">
                Counts update as soon as a stock transaction commits.
              </p>
            </div>
            <button type="button" onClick={() => onNavigate('low-stock')} className="text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-5160" data-mp="1g0xmoxu1u57h">
              Review stock alerts
            </button>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-5161" data-mp="169jd1x8dvp0m">
            <AlertRow label="Out of stock" count={stats.outOfStock.length} tone="red" onClick={() => onNavigate('low-stock')} data-id="element-5162" data-mp="1b1uex5mopkvt" />
            <AlertRow label="At or below reorder level" count={stats.lowStock.length} tone="amber" onClick={() => onNavigate('low-stock')} data-id="element-5163" data-mp="11aztu4l6crby" />
            <AlertRow label="Expired — dispensing blocked" count={stats.expired.length} tone="red" onClick={() => onNavigate('expired')} data-id="element-5164" data-mp="sa1lxb7m7zo3" />
            <AlertRow label="Expiry alerts · 30 / 60 / 90 days" count={stats.days30.length + stats.days60.length + stats.days90.length} tone="amber" detail={`${stats.days30.length} at 30d · ${stats.days60.length} at 60d · ${stats.days90.length} at 90d`} onClick={() => onNavigate('expired')} data-id="element-5165" data-mp="12vb62u0fec7r" />
          </div>
        </div>
        <aside className="space-y-5" data-id="element-5166" data-mp="k1fdpoybdlin">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5167" data-mp="f3xdqo29ygms">
            <div className="flex items-center gap-3" data-id="element-5168" data-mp="b33qlwwnp5uy">
              <span className="rounded-lg bg-teal-50 p-2 text-teal-700" data-id="element-5169" data-mp="b4ojzj78iir8">
                <ShieldCheckIcon className="h-5 w-5" data-id="element-5170" data-mp="62pg887l1jtr" />
              </span>
              <div data-id="element-5171" data-mp="144eu5glgu3q">
                <h3 className="font-bold text-slate-900" data-id="element-5172" data-mp="15pajyenaj36">
                  Medicine return policy
                </h3>
                <p className="text-sm text-slate-500" data-id="element-5173" data-mp="2crhn33q6vu0p">
                  Last updated by {pharmacySettings.updatedByName}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-3" data-id="element-5174" data-mp="2mr8lhghvet62">
              <span className="text-sm font-medium text-slate-700" data-id="element-5175" data-mp="2qqhegj536n0k">
                Allow medicine returns
              </span>
              <button type="button" role="switch" aria-checked={pharmacySettings.allowMedicineReturns} disabled={!manager || savingPolicy} onClick={() => updatePolicy(!pharmacySettings.allowMedicineReturns)} className={`relative h-6 w-11 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${pharmacySettings.allowMedicineReturns ? 'bg-teal-600' : 'bg-slate-300'}`} data-id="element-5176" data-mp="1tzf4hmtzm3nt">
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${pharmacySettings.allowMedicineReturns ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-5177" data-mp="2r1ud1r36es1x" />
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500" data-id="element-5178" data-mp="3006aabyjso3c">
              {manager ? 'Only Pharmacy Managers can change this hospital policy.' : pharmacySettings.allowMedicineReturns ? 'Returns are enabled by the current hospital policy.' : 'Returns are disabled by the current hospital policy.'}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5179" data-mp="2h9euosnyw1gi">
            <h3 className="font-bold text-slate-900" data-id="element-5180" data-mp="2m3j0h8lhy3g0">Today’s desk</h3>
            <div className="mt-4 space-y-3" data-id="element-5181" data-mp="1olsrvmmkd0up">
              <DeskMetric label="Prescriptions waiting" value={stats.pendingPrescriptions} onClick={() => onNavigate('dispensing')} data-id="element-5182" data-mp="1okzsj6pxpdml" />
              <DeskMetric label="Pending purchase orders" value={stats.pendingOrders} onClick={() => onNavigate('purchase-orders')} data-id="element-5183" data-mp="1m0bhf7d4dojc" />
              <DeskMetric label="Stock received today" value={stats.todayReceived.length} onClick={() => onNavigate('movement-history')} data-id="element-5184" data-mp="1c6b5omnc1l1n" />
            </div>
          </div>
        </aside>
      </section>
    </div>;
}
function AlertRow({
  label,
  count,
  tone,
  detail,
  onClick
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber';
  detail?: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-slate-50" data-id="element-5185" data-mp="ofoyexl5k4sp">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone === 'red' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`} data-id="element-5186" data-mp="keve0e6x584k">
        <AlertTriangleIcon className="h-4 w-4" data-id="element-5187" data-mp="5p242x8lz2z2" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-5188" data-mp="9t00v1ago1oy">
        <span className="block text-sm font-semibold text-slate-900" data-id="element-5189" data-mp="9pua7hm6orr4">
          {label}
        </span>
        <span className="mt-0.5 block text-sm text-slate-500" data-id="element-5190" data-mp="3rzo7wwp2myx9">
          {detail || 'Live inventory control indicator'}
        </span>
      </span>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-5191" data-mp="ogl4cwb902xe">
        {count}
      </span>
    </button>;
}
function DeskMetric({
  label,
  value,
  onClick
}: {
  label: string;
  value: number;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left hover:border-blue-200 hover:bg-blue-50/40" data-id="element-5192" data-mp="joa4ftgich4u">
      <span className="text-sm font-medium text-slate-700" data-id="element-5193" data-mp="1c88o8ua3edq6">{label}</span>
      <span className="text-xl font-bold text-slate-950" data-id="element-5194" data-mp="2wv9rfsug0qlz">{value}</span>
    </button>;
}
function iconTone(tone: string) {
  return {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    teal: 'bg-teal-50 text-teal-700'
  }[tone] || 'bg-slate-100 text-slate-700';
}